import React from 'react';
import styled, { css } from 'styled-components';

import { colors } from 'shared/themes';
import { Typography } from 'src/typography';
import { BankActions } from 'src/payments/components/bank-actions';
import { CircleBankLogo } from 'src/payments/components/circle-bank-logo';
import type { BankDetailResponse } from 'src/payments/utils/bank-utils';

type EnrolledBankCardProps = {
  bankDetails: BankDetailResponse;
  bankNeedsRelinking: boolean;
  onEditBank: () => void;
  onRelinkBank: () => void;
};

export function EnrolledBankCard({
  bankDetails,
  bankNeedsRelinking,
  onEditBank,
  onRelinkBank,
}: EnrolledBankCardProps): JSX.Element {
  const { bankLogo, bankName = '', lastDigits } = bankDetails;

  return (
    <Container hasError={bankNeedsRelinking}>
      <BankLogoAndDetails>
        <CircleBankLogo
          altText={`${bankName} Logo`}
          logo={bankLogo}
          circleDiameter={32}
          iconHeight={26}
          iconWidth={26}
        />
        <BankNameAndDigits>
          <BankName weight='semi' size='medium'>
            {bankName}
          </BankName>
          <BankDigits size='small'>ending in ‧‧‧‧ {lastDigits}</BankDigits>
        </BankNameAndDigits>
      </BankLogoAndDetails>
      <BankActions
        bankNeedsRelinking={bankNeedsRelinking}
        editBankAccount={onEditBank}
        relinkBankAccount={onRelinkBank}
      />
    </Container>
  );
}

const Container = styled.div<{ hasError?: boolean }>`
  ${({ theme: { spaces }, hasError }) => css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    align-self: stretch;
    border-radius: 5px;
    border: 1px solid ${hasError ? '#D34A4A' : colors.grey[85]};
    padding: ${spaces[4]} ${spaces[3]};
  `}
`;

const BankLogoAndDetails = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme: { spaces } }) => spaces[2]};
`;

const BankNameAndDigits = styled.div`
  display: flex;
  flex-direction: column;
`;

const BankName = styled(Typography.Body)`
  color: #393d40;
  line-height: 20px;
`;

const BankDigits = styled(Typography.Body)`
  color: #4b5257;
  line-height: 120%;
`;
